<script setup lang="ts">
  import { computed } from 'vue';
  import { Checkbox, IconButton, InfoTooltip, Tooltip } from '@/shared/ui';
  import { formatInstantToPtBR } from '@/shared/lib';
  import { usePermission } from '@/entities/Permission';
  import type { TrainingMatrixRenderRow } from '../lib/buildMatrixRows';
  import PencilIcon from '@/shared/assets/icons/PencilBoldIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashBoldIcon.svg';
  import InfoIcon from '@/shared/assets/icons/InfoBoldIcon.svg';

  interface Props {
    row: TrainingMatrixRenderRow;
    isSelected: boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    'toggle-selection': [positionId: number];
    'edit-clicked': [positionId: number];
    'delete-clicked': [positionId: number];
  }>();

  const { can } = usePermission();
  const canManageMatrix = computed(() => can('readMatrix', 'Training').value);
  const hasLinkedDocuments = computed(() => props.row.documents.length > 0);

  const positionAudit = computed(() => ({
    createdAt: formatInstantToPtBR(props.row.audit.createdAt),
    createdBy: props.row.audit.createdBy?.name,
    updatedAt: formatInstantToPtBR(props.row.audit.updatedAt),
    updatedBy: props.row.audit.updatedBy?.name,
  }));

  const handleToggle = () => {
    emit('toggle-selection', props.row.positionId);
  };

  const handleEdit = () => {
    emit('edit-clicked', props.row.positionId);
  };

  const handleDelete = () => {
    if (!hasLinkedDocuments.value) return;
    emit('delete-clicked', props.row.positionId);
  };
</script>

<template>
  <tr class="border-b border-gray-02 hover:bg-gray-01 transition-colors">
    <td class="px-4 py-3 w-12 align-middle text-center">
      <Checkbox
        :id="row.positionId"
        :checked="isSelected"
        @toggle="handleToggle"
      />
    </td>
    <td class="px-4 py-3 text-sm text-gray-07 align-middle">
      {{ row.positionName }}
    </td>
    <td class="px-4 py-3 text-sm text-gray-07 align-top">
      <div v-if="!hasLinkedDocuments">
        -
      </div>
      <div
        v-for="document in row.documents"
        :key="document.id"
      >
        {{ document.code }}
      </div>
    </td>
    <td class="px-4 py-3 text-sm text-gray-07 align-top">
      <div v-if="!hasLinkedDocuments">
        -
      </div>
      <div
        v-for="document in row.documents"
        :key="document.id"
      >
        {{ document.name }}
      </div>
    </td>
    <td class="px-4 py-3 align-middle">
      <div class="flex gap-1 items-center text-gray-xx-02">
        <template v-if="canManageMatrix">
          <Tooltip
            content="Editar"
            class="flex items-center"
          >
            <IconButton
              :src="PencilIcon"
              alt="Editar"
              class="w-6 h-6"
              :on-click="handleEdit"
            />
          </Tooltip>
          <Tooltip
            content="Deletar"
            class="flex items-center"
          >
            <IconButton
              :src="TrashIcon"
              alt="Deletar"
              class="w-6 h-6"
              :class="{ 'opacity-40 pointer-events-none': !hasLinkedDocuments }"
              :on-click="handleDelete"
            />
          </Tooltip>
        </template>
        <InfoTooltip
          :icon="InfoIcon"
          :created-at="positionAudit.createdAt"
          :created-by="positionAudit.createdBy"
          :updated-at="positionAudit.updatedAt"
          :updated-by="positionAudit.updatedBy"
        >
          <IconButton
            :src="InfoIcon"
            alt="Informações"
            class="w-6 h-6"
          />
        </InfoTooltip>
      </div>
    </td>
  </tr>
</template>
